import React, { useState, useEffect, useCallback } from 'react';
import Header from '../../components/ui/Header';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import { invoiceService } from '../../services/invoiceService';
import { useNavigate } from 'react-router-dom';
import PayTRWebhookTracker from './components/PayTRWebhookTracker';

const STATUS_LABELS = {
  success: { label: 'Başarılı', color: 'bg-green-100 text-green-700 border-green-200' },
  error: { label: 'Hata', color: 'bg-red-100 text-red-700 border-red-200' },
  skipped: { label: 'Atlandı', color: 'bg-yellow-100 text-yellow-700 border-yellow-200' },
};

const ERROR_STAGE_LABELS = {
  order_fetch: 'Sipariş Getirme',
  items_fetch: 'Ürün Getirme',
  oauth: 'OAuth Kimlik Doğrulama',
  invoice_draft: 'Fatura Taslağı',
  unknown: 'Bilinmiyor',
};

// ─── Helpers ────────────────────────────────────────────────────────────────

/**
 * Returns true if the log is an "orphan" (no real order context).
 * Orphans: missing order_id (null/undefined/empty).
 */
const isOrphan = (log) => !log?.order_id;

/**
 * Extracts the best available value for a field from an array of log entries.
 * Iterates through all entries and returns the first non-null, non-empty value found.
 */
const extractBestValue = (entries, field) => {
  for (const entry of entries) {
    const val = entry?.[field];
    if (val !== null && val !== undefined && val !== '') {
      return val;
    }
  }
  return null;
};

/**
 * Group an array of logs by order_id.
 * Logs without order_id are grouped under their own id (orphan).
 * Returns an array of group objects sorted by latest attempt desc.
 * Smart extraction: customer metadata is pulled from the first valid entry,
 * not necessarily the latest attempt.
 */
const groupLogsByOrder = (logs) => {
  const map = new Map();
  for (const log of logs) {
    const key = log?.order_id || `__orphan__${log?.id}`;
    if (!map?.has(key)) {
      map?.set(key, []);
    }
    map?.get(key)?.push(log);
  }

  const groups = [];
  for (const [key, entries] of map?.entries()) {
    // Sort attempts chronologically (oldest first) for history display
    const sorted = [...entries]?.sort(
      (a, b) => new Date(a?.created_at) - new Date(b?.created_at)
    );
    // Latest attempt = last in sorted array
    const latest = sorted?.[sorted?.length - 1];

    // Smart extraction: find the first valid value for each metadata field
    // across ALL attempts, not just the latest one
    const bestCustomerName = extractBestValue(sorted, 'customer_name');
    const bestOrderNumber = extractBestValue(sorted, 'order_number');
    const bestTotalAmount = extractBestValue(sorted, 'total_amount');
    const bestCustomerVkn = extractBestValue(sorted, 'customer_vkn_tckn');
    const bestDocNo = extractBestValue(sorted, 'doc_no');

    groups?.push({
      key,
      orderId: latest?.order_id || null,
      orderNumber: bestOrderNumber,
      customerName: bestCustomerName,
      customerVkn: bestCustomerVkn,
      totalAmount: bestTotalAmount,
      bestDocNo: bestDocNo,
      latestStatus: latest?.status,
      latestLog: latest,
      attempts: sorted,
      isOrphan: !latest?.order_id,
    });
  }

  // Sort groups by latest attempt date desc
  groups?.sort(
    (a, b) =>
      new Date(b?.latestLog?.created_at) - new Date(a?.latestLog?.created_at)
  );
  return groups;
};

// ─── Sub-components ──────────────────────────────────────────────────────────

const StatCard = ({ title, value, icon, iconBg, subtitle }) => (
  <div className="bg-card rounded-xl border border-border p-5 flex items-center gap-4">
    <div className={`w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 ${iconBg}`}>
      <Icon name={icon} size={22} />
    </div>
    <div>
      <p className="text-sm text-muted-foreground">{title}</p>
      <p className="text-2xl font-bold text-foreground">{value}</p>
      {subtitle && <p className="text-xs text-muted-foreground mt-0.5">{subtitle}</p>}
    </div>
  </div>
);

const AutoRetryResultModal = ({ result, onClose }) => {
  if (!result) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="bg-card rounded-2xl border border-border w-full max-w-lg shadow-xl">
        <div className="flex items-center justify-between p-6 border-b border-border">
          <h2 className="text-lg font-semibold text-foreground">Otomatik Yeniden Deneme Sonuçları</h2>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-muted transition-colors">
            <Icon name="X" size={18} />
          </button>
        </div>
        <div className="p-6 space-y-4">
          <div className="grid grid-cols-3 gap-3">
            <div className="bg-muted/40 rounded-xl p-3 text-center">
              <p className="text-2xl font-bold text-foreground">{result?.attempted}</p>
              <p className="text-xs text-muted-foreground mt-1">Denendi</p>
            </div>
            <div className="bg-green-50 border border-green-200 rounded-xl p-3 text-center">
              <p className="text-2xl font-bold text-green-700">{result?.succeeded}</p>
              <p className="text-xs text-green-600 mt-1">Başarılı</p>
            </div>
            <div className="bg-red-50 border border-red-200 rounded-xl p-3 text-center">
              <p className="text-2xl font-bold text-red-700">{result?.failed}</p>
              <p className="text-xs text-red-600 mt-1">Başarısız</p>
            </div>
          </div>
          {result?.results?.length > 0 && (
            <div className="max-h-60 overflow-y-auto space-y-2">
              {result?.results?.map((r, i) => (
                <div key={i} className={`flex items-center justify-between px-3 py-2 rounded-lg text-sm border ${r?.status === 'success' ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'}`}>
                  <span className={`font-medium ${r?.status === 'success' ? 'text-green-700' : 'text-red-700'}`}>
                    {r?.orderNumber || r?.logId?.slice(0, 8)}
                  </span>
                  <div className="flex items-center gap-1.5">
                    <Icon name={r?.status === 'success' ? 'CheckCircle' : 'XCircle'} size={14} className={r?.status === 'success' ? 'text-green-600' : 'text-red-600'} />
                    <span className={r?.status === 'success' ? 'text-green-600' : 'text-red-600'}>
                      {r?.status === 'success' ? 'Başarılı' : r?.error || 'Başarısız'}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
        <div className="px-6 pb-6">
          <Button variant="default" onClick={onClose} className="w-full">Kapat</Button>
        </div>
      </div>
    </div>
  );
};

const InvoiceDetailModal = ({ log, onClose, onRetry, retryingId }) => {
  if (!log) return null;
  const isRetrying = retryingId === log?.id;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="bg-card rounded-2xl border border-border w-full max-w-2xl max-h-[90vh] overflow-y-auto shadow-xl">
        <div className="flex items-center justify-between p-6 border-b border-border">
          <div>
            <h2 className="text-lg font-semibold text-foreground">Fatura Detayı</h2>
            <p className="text-sm text-muted-foreground mt-0.5">#{log?.order_number || log?.id?.slice(0, 8)}</p>
          </div>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-muted transition-colors">
            <Icon name="X" size={18} />
          </button>
        </div>

        <div className="p-6 space-y-5">
          {/* Status */}
          <div className="flex items-center gap-3 flex-wrap">
            <span className={`px-3 py-1 rounded-full text-xs font-semibold border ${STATUS_LABELS?.[log?.status]?.color}`}>
              {STATUS_LABELS?.[log?.status]?.label || log?.status}
            </span>
            {log?.is_retry && (
              <span className="px-3 py-1 rounded-full text-xs font-semibold border bg-blue-50 text-blue-700 border-blue-200">
                Yeniden Deneme #{log?.retry_count || 1}
              </span>
            )}
            <span className="text-sm text-muted-foreground">
              {log?.created_at ? new Date(log.created_at)?.toLocaleString('tr-TR') : '-'}
            </span>
          </div>

          {/* Retry Info */}
          {(log?.retry_count > 0 || log?.last_retry_at) && (
            <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
              <h3 className="text-sm font-semibold text-blue-700 mb-2 flex items-center gap-2">
                <Icon name="RefreshCw" size={14} />
                Yeniden Deneme Bilgisi
              </h3>
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div>
                  <p className="text-blue-600">Toplam Deneme</p>
                  <p className="font-medium text-blue-800">{log?.retry_count || 0}</p>
                </div>
                {log?.last_retry_at && (
                  <div>
                    <p className="text-blue-600">Son Deneme</p>
                    <p className="font-medium text-blue-800">{new Date(log?.last_retry_at)?.toLocaleString('tr-TR')}</p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Customer Info */}
          <div className="bg-muted/40 rounded-xl p-4 space-y-2">
            <h3 className="text-sm font-semibold text-foreground mb-3">Müşteri Bilgileri</h3>
            <div className="grid grid-cols-2 gap-3 text-sm">
              <div>
                <p className="text-muted-foreground">Ad Soyad</p>
                <p className="font-medium text-foreground">{log?.customer_name || '-'}</p>
              </div>
              <div>
                <p className="text-muted-foreground">VKN / TCKN</p>
                <p className="font-medium text-foreground">{log?.customer_vkn_tckn || '-'}</p>
              </div>
              <div>
                <p className="text-muted-foreground">Sipariş No</p>
                <p className="font-medium text-foreground">{log?.order_number || '-'}</p>
              </div>
              <div>
                <p className="text-muted-foreground">Tutar</p>
                <p className="font-medium text-foreground">
                  {log?.total_amount != null ? `₺${parseFloat(log?.total_amount)?.toLocaleString('tr-TR', { minimumFractionDigits: 2 })}` : '-'}
                </p>
              </div>
            </div>
          </div>

          {/* Invoice Info */}
          {(log?.doc_no || log?.ettn || log?.e_document_type) && (
            <div className="bg-muted/40 rounded-xl p-4 space-y-2">
              <h3 className="text-sm font-semibold text-foreground mb-3">Fatura Bilgileri</h3>
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div>
                  <p className="text-muted-foreground">Belge Tipi</p>
                  <p className="font-medium text-foreground">{log?.e_document_type || '-'}</p>
                </div>
                <div>
                  <p className="text-muted-foreground">Belge No</p>
                  <p className="font-medium text-foreground">{log?.doc_no || '-'}</p>
                </div>
                <div className="col-span-2">
                  <p className="text-muted-foreground">ETTN</p>
                  <p className="font-medium text-foreground break-all">{log?.ettn || '-'}</p>
                </div>
              </div>
            </div>
          )}

          {/* Error Info */}
          {log?.status === 'error' && (
            <div className="bg-red-50 border border-red-200 rounded-xl p-4">
              <h3 className="text-sm font-semibold text-red-700 mb-2">Hata Bilgisi</h3>
              <div className="space-y-2 text-sm">
                <div>
                  <p className="text-red-600 font-medium">Aşama</p>
                  <p className="text-red-700">{ERROR_STAGE_LABELS?.[log?.error_stage] || log?.error_stage || '-'}</p>
                </div>
                <div>
                  <p className="text-red-600 font-medium">Mesaj</p>
                  <p className="text-red-700 break-words">{log?.error_message || '-'}</p>
                </div>
              </div>
            </div>
          )}

          {/* Mysoft Response */}
          {log?.mysoft_response && (
            <div>
              <h3 className="text-sm font-semibold text-foreground mb-2">Mysoft Yanıtı</h3>
              <pre className="bg-muted rounded-lg p-3 text-xs text-foreground overflow-x-auto whitespace-pre-wrap break-all">
                {JSON.stringify(log?.mysoft_response, null, 2)}
              </pre>
            </div>
          )}

          {/* Retry Action */}
          {log?.status === 'error' && log?.order_id && (
            <div className="pt-2 border-t border-border">
              <Button
                variant="default"
                iconName={isRetrying ? 'Loader' : 'RefreshCw'}
                iconPosition="left"
                disabled={isRetrying}
                onClick={() => onRetry?.(log)}
                className="w-full"
              >
                {isRetrying ? 'Yeniden Deneniyor...' : 'Manuel Yeniden Dene'}
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// ─── Accordion Row ───────────────────────────────────────────────────────────

const OrderAccordionRow = ({ group, onViewDetail, onRetry, onSendEmail, retryingId, sendingEmailId, setEmailModalLog, setEmailInput }) => {
  const [expanded, setExpanded] = useState(false);
  const { latestLog, latestStatus, attempts, orderNumber, customerName, customerVkn, totalAmount, bestDocNo } = group;

  return (
    <>
      {/* Parent Row */}
      <tr
        className={`hover:bg-muted/30 transition-colors cursor-pointer ${expanded ? 'bg-muted/20' : ''}`}
        onClick={() => setExpanded((v) => !v)}
      >
        {/* Expand toggle */}
        <td className="px-3 py-3 w-8">
          <div className={`transition-transform duration-200 text-muted-foreground ${expanded ? 'rotate-180' : ''}`}>
            <Icon name="ChevronDown" size={16} />
          </div>
        </td>
        {/* Status */}
        <td className="px-4 py-3">
          <div className="flex flex-col gap-1">
            <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold border w-fit ${STATUS_LABELS?.[latestStatus]?.color}`}>
              <Icon
                name={latestStatus === 'success' ? 'CheckCircle' : latestStatus === 'error' ? 'XCircle' : 'MinusCircle'}
                size={12}
              />
              {STATUS_LABELS?.[latestStatus]?.label || latestStatus}
            </span>
            {attempts?.length > 1 && (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-blue-50 text-blue-600 border border-blue-200 w-fit">
                <Icon name="RefreshCw" size={10} />
                {attempts?.length} deneme
              </span>
            )}
            {latestLog?.email_sent && (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-teal-50 text-teal-700 border border-teal-200 w-fit">
                <Icon name="Mail" size={10} />
                Email Sent
              </span>
            )}
          </div>
        </td>
        {/* Order No */}
        <td className="px-4 py-3 font-medium text-foreground">{orderNumber || '-'}</td>
        {/* Customer */}
        <td className="px-4 py-3">
          <p className="text-foreground font-medium">{customerName || '-'}</p>
          {customerVkn && <p className="text-xs text-muted-foreground">{customerVkn}</p>}
        </td>
        {/* Doc No — use bestDocNo (first valid across all attempts) */}
        <td className="px-4 py-3 text-muted-foreground font-mono text-xs">{bestDocNo || '-'}</td>
        {/* Amount */}
        <td className="px-4 py-3 text-foreground">
          {totalAmount != null
            ? `₺${parseFloat(totalAmount)?.toLocaleString('tr-TR', { minimumFractionDigits: 2 })}`
            : '-'}
        </td>
        {/* Error Stage (latest) */}
        <td className="px-4 py-3">
          {latestLog?.error_stage ? (
            <span className="text-xs text-red-600 bg-red-50 border border-red-200 px-2 py-0.5 rounded">
              {ERROR_STAGE_LABELS?.[latestLog?.error_stage] || latestLog?.error_stage}
            </span>
          ) : '-'}
        </td>
        {/* Latest date */}
        <td className="px-4 py-3 text-muted-foreground text-xs whitespace-nowrap">
          {latestLog?.created_at ? new Date(latestLog.created_at)?.toLocaleString('tr-TR') : '-'}
        </td>
        {/* Actions */}
        <td className="px-4 py-3" onClick={(e) => e?.stopPropagation()}>
          <div className="flex items-center gap-1">
            <button
              onClick={() => {
                if (latestLog?.pdf_url) {
                  window.open(latestLog?.pdf_url, '_blank', 'noopener,noreferrer');
                } else {
                  onViewDetail(latestLog);
                }
              }}
              className={`p-1.5 rounded-lg hover:bg-muted transition-colors ${latestLog?.pdf_url ? 'text-green-600 hover:text-green-700 hover:bg-green-50' : 'text-muted-foreground hover:text-foreground'}`}
              title={latestLog?.pdf_url ? 'PDF Görüntüle' : 'Detay'}
            >
              <Icon name="Eye" size={15} />
            </button>
            <button
              onClick={() => onViewDetail(latestLog)}
              className="p-1.5 rounded-lg hover:bg-muted transition-colors text-muted-foreground hover:text-foreground"
              title="Detay"
            >
              <Icon name="FileText" size={15} />
            </button>
            {latestStatus === 'error' && latestLog?.order_id && (
              <button
                onClick={() => onRetry(latestLog)}
                disabled={!!retryingId}
                className="p-1.5 rounded-lg hover:bg-orange-50 transition-colors text-orange-500 hover:text-orange-700 disabled:opacity-40 disabled:cursor-not-allowed"
                title="Yeniden Dene"
              >
                <Icon name={retryingId === latestLog?.id ? 'Loader' : 'RotateCcw'} size={15} className={retryingId === latestLog?.id ? 'animate-spin' : ''} />
              </button>
            )}
            {latestStatus === 'success' && (
              <button
                onClick={() => { setEmailModalLog(latestLog); setEmailInput(latestLog?.customer_email || ''); }}
                disabled={sendingEmailId === latestLog?.id}
                className={`p-1.5 rounded-lg transition-colors disabled:opacity-40 disabled:cursor-not-allowed ${latestLog?.email_sent ? 'text-teal-600 hover:bg-teal-50 hover:text-teal-700' : 'text-blue-500 hover:bg-blue-50 hover:text-blue-700'}`}
                title={latestLog?.email_sent ? 'Tekrar Gönder' : 'Müşteriye Gönder'}
              >
                <Icon name={sendingEmailId === latestLog?.id ? 'Loader' : 'Send'} size={15} className={sendingEmailId === latestLog?.id ? 'animate-spin' : ''} />
              </button>
            )}
          </div>
        </td>
      </tr>
      {/* Expanded: Attempt History */}
      {expanded && (
        <tr>
          <td colSpan={9} className="px-0 py-0 bg-muted/10 border-b border-border">
            <div className="px-8 py-3">
              <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2 flex items-center gap-1.5">
                <Icon name="History" size={12} />
                Deneme Geçmişi ({attempts?.length} deneme)
              </p>
              <div className="rounded-lg border border-border overflow-hidden">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="bg-muted/60">
                      <th className="text-left px-4 py-2 text-muted-foreground font-semibold">#</th>
                      <th className="text-left px-4 py-2 text-muted-foreground font-semibold">Tarih</th>
                      <th className="text-left px-4 py-2 text-muted-foreground font-semibold">Durum</th>
                      <th className="text-left px-4 py-2 text-muted-foreground font-semibold">Hata Aşaması</th>
                      <th className="text-left px-4 py-2 text-muted-foreground font-semibold">Hata Mesajı</th>
                      <th className="text-left px-4 py-2 text-muted-foreground font-semibold">Belge No</th>
                      <th className="text-left px-4 py-2 text-muted-foreground font-semibold"></th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {attempts?.map((attempt, idx) => (
                      <tr key={attempt?.id} className={`${idx === attempts?.length - 1 ? 'bg-blue-50/40' : 'bg-card'} hover:bg-muted/20 transition-colors`}>
                        <td className="px-4 py-2 text-muted-foreground font-mono">
                          {idx + 1}
                          {idx === attempts?.length - 1 && (
                            <span className="ml-1 text-blue-600 font-semibold">(son)</span>
                          )}
                        </td>
                        <td className="px-4 py-2 text-muted-foreground whitespace-nowrap">
                          {attempt?.created_at ? new Date(attempt.created_at)?.toLocaleString('tr-TR') : '-'}
                        </td>
                        <td className="px-4 py-2">
                          <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold border ${STATUS_LABELS?.[attempt?.status]?.color}`}>
                            <Icon
                              name={attempt?.status === 'success' ? 'CheckCircle' : attempt?.status === 'error' ? 'XCircle' : 'MinusCircle'}
                              size={10}
                            />
                            {STATUS_LABELS?.[attempt?.status]?.label || attempt?.status}
                          </span>
                        </td>
                        <td className="px-4 py-2">
                          {attempt?.error_stage ? (
                            <span className="text-red-600 bg-red-50 border border-red-200 px-1.5 py-0.5 rounded text-xs">
                              {ERROR_STAGE_LABELS?.[attempt?.error_stage] || attempt?.error_stage}
                            </span>
                          ) : '-'}
                        </td>
                        <td className="px-4 py-2 text-muted-foreground max-w-xs truncate" title={attempt?.error_message}>
                          {attempt?.error_message || '-'}
                        </td>
                        <td className="px-4 py-2 font-mono text-muted-foreground">{attempt?.doc_no || '-'}</td>
                        <td className="px-4 py-2">
                          <button
                            onClick={() => onViewDetail(attempt)}
                            className="p-1 rounded hover:bg-muted transition-colors text-muted-foreground hover:text-foreground"
                            title="Detay"
                          >
                            <Icon name="ExternalLink" size={12} />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </td>
        </tr>
      )}
    </>
  );
};

// ─── Main Component ──────────────────────────────────────────────────────────

const InvoiceAuditDashboard = ({ embedded = false }) => {
  const navigate = useNavigate();
  const [logs, setLogs] = useState([]);
  const [stats, setStats] = useState(null);
  const [errorBreakdown, setErrorBreakdown] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statsLoading, setStatsLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedLog, setSelectedLog] = useState(null);

  // Active tab: 'orders' | 'system'
  const [activeTab, setActiveTab] = useState('orders');

  // Retry state
  const [retryingId, setRetryingId] = useState(null);
  const [retryToast, setRetryToast] = useState(null);
  const [autoRetrying, setAutoRetrying] = useState(false);
  const [autoRetryResult, setAutoRetryResult] = useState(null);

  // Email send state
  const [sendingEmailId, setSendingEmailId] = useState(null);
  const [emailModalLog, setEmailModalLog] = useState(null);
  const [emailInput, setEmailInput] = useState('');

  // Filters
  const [statusFilter, setStatusFilter] = useState('');
  const [search, setSearch] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [page, setPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const PAGE_SIZE = 100; // Fetch more so grouping works across pages

  const [quickRetryOrderId, setQuickRetryOrderId] = useState('85113f25');
  const [quickRetrying, setQuickRetrying] = useState(false);

  useEffect(() => {
    const userRole = localStorage.getItem('userRole');
    if (userRole !== 'admin') {
      navigate('/event-discovery');
    }
  }, [navigate]);

  const loadStats = useCallback(async () => {
    try {
      setStatsLoading(true);
      const [statsData, breakdown] = await Promise.all([
        invoiceService?.getInvoiceStats(),
        invoiceService?.getErrorBreakdown(),
      ]);
      setStats(statsData);
      setErrorBreakdown(breakdown);
    } catch (err) {
      console.error('Stats load error:', err);
    } finally {
      setStatsLoading(false);
    }
  }, []);

  const loadLogs = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const { data, count } = await invoiceService?.getInvoiceLogs({
        page,
        pageSize: PAGE_SIZE,
        status: statusFilter || null,
        search,
        dateFrom: dateFrom ? new Date(dateFrom)?.toISOString() : null,
        dateTo: dateTo ? new Date(dateTo + 'T23:59:59')?.toISOString() : null,
      });
      setLogs(data);
      setTotalCount(count);
    } catch (err) {
      console.error('Logs load error:', err);
      setError(err?.message || 'Fatura kayıtları yüklenemedi');
    } finally {
      setLoading(false);
    }
  }, [page, statusFilter, search, dateFrom, dateTo]);

  useEffect(() => { loadStats(); }, [loadStats]);
  useEffect(() => { loadLogs(); }, [loadLogs]);

  // ── Derived data ──────────────────────────────────────────────────────────

  const orderLogs = logs?.filter((l) => !isOrphan(l));
  const orphanLogs = logs?.filter((l) => isOrphan(l));
  const orderGroups = groupLogsByOrder(orderLogs);

  // Unique-order-based metrics
  const uniqueOrderCount = orderGroups?.length;
  const uniqueSuccessCount = orderGroups?.filter((g) => g?.latestStatus === 'success')?.length;
  const uniqueErrorCount = orderGroups?.filter((g) => g?.latestStatus === 'error')?.length;
  const uniqueSkippedCount = orderGroups?.filter((g) => g?.latestStatus === 'skipped')?.length;
  const successAmount = orderGroups
    ?.filter((g) => g?.latestStatus === 'success')
    ?.reduce((sum, g) => sum + (parseFloat(g?.totalAmount) || 0), 0);

  // ── Handlers ──────────────────────────────────────────────────────────────

  const showToast = (message, type = 'success') => {
    setRetryToast({ message, type });
    setTimeout(() => setRetryToast(null), 4000);
  };

  const handleQuickRetry = async (e) => {
    e?.preventDefault();
    if (!quickRetryOrderId?.trim() || quickRetrying) return;
    setQuickRetrying(true);
    try {
      const result = await invoiceService?.retryByOrderId(quickRetryOrderId?.trim());
      if (result?.success) {
        showToast(`Fatura başarıyla yeniden gönderildi: ${quickRetryOrderId}`, 'success');
        setQuickRetryOrderId('');
        loadLogs();
        loadStats();
      } else {
        showToast(`Yeniden deneme başarısız: ${result?.error || 'Bilinmeyen hata'}`, 'error');
      }
    } catch (err) {
      showToast(`Hata: ${err?.message}`, 'error');
    } finally {
      setQuickRetrying(false);
    }
  };

  const handleRetry = async (log) => {
    if (retryingId) return;
    setRetryingId(log?.id);
    try {
      const result = await invoiceService?.retryInvoice(log);
      if (result?.success) {
        showToast(`Fatura başarıyla yeniden gönderildi: ${log?.order_number || log?.id?.slice(0, 8)}`, 'success');
        setSelectedLog(null);
        loadLogs();
        loadStats();
      } else {
        showToast(`Yeniden deneme başarısız: ${result?.error || 'Bilinmeyen hata'}`, 'error');
      }
    } catch (err) {
      showToast(`Hata: ${err?.message}`, 'error');
    } finally {
      setRetryingId(null);
    }
  };

  const handleAutoRetry = async () => {
    if (autoRetrying) return;
    setAutoRetrying(true);
    try {
      const result = await invoiceService?.autoRetryFailedInvoices({ maxRetries: 3 });
      setAutoRetryResult(result);
      if (result?.succeeded > 0) {
        loadLogs();
        loadStats();
      }
    } catch (err) {
      showToast(`Otomatik yeniden deneme hatası: ${err?.message}`, 'error');
    } finally {
      setAutoRetrying(false);
    }
  };

  const handleSearch = (e) => {
    e?.preventDefault();
    setPage(1);
    loadLogs();
  };

  const handleFilterChange = (key, value) => {
    setPage(1);
    if (key === 'status') setStatusFilter(value);
    if (key === 'dateFrom') setDateFrom(value);
    if (key === 'dateTo') setDateTo(value);
  };

  const handleClearFilters = () => {
    setStatusFilter('');
    setSearch('');
    setDateFrom('');
    setDateTo('');
    setPage(1);
  };

  const handleSendEmail = async (log) => {
    if (!emailInput?.trim()) return;
    setSendingEmailId(log?.id);
    try {
      const result = await invoiceService?.sendInvoiceEmail(log, emailInput?.trim());
      if (result?.success) {
        showToast(`E-fatura ${log?.customer_name || ''} adresine başarıyla gönderildi`, 'success');
        setEmailModalLog(null);
        setEmailInput('');
        loadLogs();
      } else {
        if (result?.earchive_response || result?.outbox_response) {
          console.group('[send-invoice-email] RAW MYSOFT RESPONSES');
          console.log('EArchive Raw Response:', result?.earchive_response ?? '(not attempted)');
          console.log('Outbox Raw Response:', result?.outbox_response ?? '(not attempted)');
          console.log('Error:', result?.error);
          console.groupEnd();
        }
        let toastMsg = `E-posta gönderilemedi: ${result?.error || 'Bilinmeyen hata'}`;
        if (result?.earchive_response) toastMsg += ` | EArchive: ${String(result?.earchive_response)?.slice(0, 120)}`;
        if (result?.outbox_response) toastMsg += ` | Outbox: ${String(result?.outbox_response)?.slice(0, 120)}`;
        showToast(toastMsg, 'error');
      }
    } catch (err) {
      showToast(`Hata: ${err?.message}`, 'error');
    } finally {
      setSendingEmailId(null);
    }
  };

  const totalPages = Math.ceil(totalCount / PAGE_SIZE);

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <div className={embedded ? '' : 'min-h-screen bg-background'}>
      {!embedded && <Header />}
      <div className={embedded ? '' : 'main-content'}>
        <div className={embedded ? '' : 'max-w-[1536px] mx-auto px-4 md:px-6 lg:px-8 py-6 md:py-8'}>

          {/* Toast */}
          {retryToast && (
            <div className={`fixed top-6 right-6 z-[9999] flex items-center gap-3 px-4 py-3 rounded-xl shadow-lg border text-sm font-medium transition-all ${retryToast?.type === 'success' ? 'bg-green-50 border-green-200 text-green-800' : 'bg-red-50 border-red-200 text-red-800'}`}>
              <Icon name={retryToast?.type === 'success' ? 'CheckCircle' : 'XCircle'} size={16} />
              {retryToast?.message}
            </div>
          )}

          {/* Page Header */}
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
            <div>
              <div className="flex items-center gap-2 mb-1">
                {!embedded && (
                  <button
                    onClick={() => navigate('/admin-dashboard')}
                    className="text-muted-foreground hover:text-foreground transition-colors"
                  >
                    <Icon name="ArrowLeft" size={18} />
                  </button>
                )}
                <h1 className="text-2xl md:text-3xl font-bold text-foreground">Fatura Denetim Paneli</h1>
              </div>
              <p className={`text-sm text-muted-foreground ${!embedded ? 'ml-7' : ''}`}>
                E-fatura gönderim geçmişi ve durum takibi
              </p>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              {uniqueErrorCount > 0 && (
                <Button
                  variant="outline"
                  iconName={autoRetrying ? 'Loader' : 'RotateCcw'}
                  iconPosition="left"
                  disabled={autoRetrying}
                  onClick={handleAutoRetry}
                  className="border-orange-300 text-orange-700 hover:bg-orange-50"
                >
                  {autoRetrying ? 'Deneniyor...' : `Hatalıları Yeniden Dene (${uniqueErrorCount})`}
                </Button>
              )}
              <Button
                variant="outline"
                iconName="RefreshCw"
                iconPosition="left"
                onClick={() => { loadStats(); loadLogs(); }}
              >
                Yenile
              </Button>
            </div>
          </div>

          {/* Quick Retry by Order ID */}
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-6">
            <div className="flex items-center gap-2 mb-2">
              <Icon name="Zap" size={16} className="text-amber-600" />
              <span className="text-sm font-semibold text-amber-800">Sipariş ID ile Hızlı Yeniden Dene</span>
            </div>
            <form onSubmit={handleQuickRetry} className="flex gap-2">
              <input
                type="text"
                value={quickRetryOrderId}
                onChange={(e) => setQuickRetryOrderId(e?.target?.value)}
                placeholder="Sipariş UUID (örn: 85113f25-...)"
                className="flex-1 px-3 py-2 text-sm bg-white border border-amber-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-400/30 text-foreground font-mono"
              />
              <button
                type="submit"
                disabled={quickRetrying || !quickRetryOrderId?.trim()}
                className="px-4 py-2 text-sm font-medium bg-amber-600 hover:bg-amber-700 disabled:opacity-50 text-white rounded-lg transition-colors flex items-center gap-2"
              >
                {quickRetrying ? <Icon name="Loader" size={14} className="animate-spin" /> : <Icon name="RotateCcw" size={14} />}
                {quickRetrying ? 'Deneniyor...' : 'Yeniden Dene'}
              </button>
            </form>
            <p className="text-xs text-amber-600 mt-1">Sipariş #85113f25 için başarısız fatura yeniden denenecek. streetName 100 karakter sınırı uygulandı.</p>
          </div>

          {/* Stats — unique-order based */}
          {statsLoading ? (
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
              {[...Array(4)]?.map((_, i) => (
                <div key={i} className="bg-card rounded-xl border border-border p-5 h-24 animate-pulse" />
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
              <StatCard
                title="Toplam Fatura"
                value={uniqueOrderCount?.toLocaleString('tr-TR')}
                icon="FileText"
                iconBg="bg-primary/10 text-primary"
                subtitle="Benzersiz sipariş"
              />
              <StatCard
                title="Başarılı"
                value={uniqueSuccessCount?.toLocaleString('tr-TR')}
                icon="CheckCircle"
                iconBg="bg-green-100 text-green-600"
                subtitle={`₺${successAmount?.toLocaleString('tr-TR', { minimumFractionDigits: 2 })}`}
              />
              <StatCard
                title="Hatalı"
                value={uniqueErrorCount?.toLocaleString('tr-TR')}
                icon="XCircle"
                iconBg="bg-red-100 text-red-600"
                subtitle={uniqueOrderCount > 0 ? `%${((uniqueErrorCount / uniqueOrderCount) * 100)?.toFixed(1)} hata oranı` : ''}
              />
              <StatCard
                title="Atlandı"
                value={uniqueSkippedCount?.toLocaleString('tr-TR')}
                icon="MinusCircle"
                iconBg="bg-yellow-100 text-yellow-600"
                subtitle=""
              />
            </div>
          )}

          {/* PayTR Real-time Webhook Tracker */}
          <div className="mb-6">
            <PayTRWebhookTracker />
          </div>

          {/* Error Breakdown */}
          {errorBreakdown?.length > 0 && (
            <div className="bg-card rounded-xl border border-border p-5 mb-6">
              <h2 className="text-base font-semibold text-foreground mb-4 flex items-center gap-2">
                <Icon name="AlertTriangle" size={16} className="text-red-500" />
                Hata Aşaması Dağılımı
              </h2>
              <div className="flex flex-wrap gap-3">
                {errorBreakdown?.map(({ stage, count }) => (
                  <div key={stage} className="flex items-center gap-2 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
                    <span className="text-sm font-medium text-red-700">{ERROR_STAGE_LABELS?.[stage] || stage}</span>
                    <span className="bg-red-200 text-red-800 text-xs font-bold px-2 py-0.5 rounded-full">{count}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Filters */}
          <div className="bg-card rounded-xl border border-border p-5 mb-6">
            <form onSubmit={handleSearch} className="flex flex-col sm:flex-row gap-3">
              <div className="flex-1 relative">
                <Icon name="Search" size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <input
                  type="text"
                  placeholder="Sipariş no, müşteri adı, belge no, ETTN..."
                  value={search}
                  onChange={(e) => setSearch(e?.target?.value)}
                  className="w-full pl-9 pr-4 py-2 text-sm bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20 text-foreground"
                />
              </div>
              <select
                value={statusFilter}
                onChange={(e) => handleFilterChange('status', e?.target?.value)}
                className="px-3 py-2 text-sm bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20 text-foreground"
              >
                <option value="">Tüm Durumlar</option>
                <option value="success">Başarılı</option>
                <option value="error">Hatalı</option>
                <option value="skipped">Atlandı</option>
              </select>
              <input
                type="date"
                value={dateFrom}
                onChange={(e) => handleFilterChange('dateFrom', e?.target?.value)}
                className="px-3 py-2 text-sm bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20 text-foreground"
              />
              <input
                type="date"
                value={dateTo}
                onChange={(e) => handleFilterChange('dateTo', e?.target?.value)}
                className="px-3 py-2 text-sm bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20 text-foreground"
              />
              <Button type="submit" variant="default" iconName="Search" iconPosition="left">
                Ara
              </Button>
              {(statusFilter || search || dateFrom || dateTo) && (
                <Button type="button" variant="outline" onClick={handleClearFilters}>
                  Temizle
                </Button>
              )}
            </form>
          </div>

          {/* Tabs */}
          <div className="flex items-center gap-1 mb-4 border-b border-border">
            <button
              onClick={() => setActiveTab('orders')}
              className={`px-4 py-2.5 text-sm font-medium rounded-t-lg transition-colors border-b-2 -mb-px ${activeTab === 'orders' ? 'border-primary text-primary bg-primary/5' : 'border-transparent text-muted-foreground hover:text-foreground'}`}
            >
              <span className="flex items-center gap-2">
                <Icon name="FileText" size={14} />
                Sipariş Faturaları
                <span className="bg-muted text-muted-foreground text-xs px-1.5 py-0.5 rounded-full font-semibold">
                  {orderGroups?.length}
                </span>
              </span>
            </button>
            <button
              onClick={() => setActiveTab('system')}
              className={`px-4 py-2.5 text-sm font-medium rounded-t-lg transition-colors border-b-2 -mb-px ${activeTab === 'system' ? 'border-orange-500 text-orange-600 bg-orange-50' : 'border-transparent text-muted-foreground hover:text-foreground'}`}
            >
              <span className="flex items-center gap-2">
                <Icon name="AlertOctagon" size={14} />
                Sistem Hataları
                {orphanLogs?.length > 0 && (
                  <span className="bg-orange-100 text-orange-700 text-xs px-1.5 py-0.5 rounded-full font-semibold">
                    {orphanLogs?.length}
                  </span>
                )}
              </span>
            </button>
          </div>

          {/* ── Tab: Order Invoices ── */}
          {activeTab === 'orders' && (
            <div className="bg-card rounded-xl border border-border overflow-hidden">
              <div className="flex items-center justify-between px-5 py-4 border-b border-border">
                <h2 className="text-base font-semibold text-foreground">
                  Sipariş Faturaları
                  <span className="ml-2 text-sm font-normal text-muted-foreground">
                    ({orderGroups?.length} benzersiz sipariş)
                  </span>
                </h2>
              </div>

              {error && (
                <div className="p-5">
                  <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-sm text-red-700">{error}</div>
                </div>
              )}

              {loading ? (
                <div className="flex items-center justify-center py-16">
                  <div className="w-8 h-8 border-4 border-primary/20 border-t-primary rounded-full animate-spin" />
                </div>
              ) : orderGroups?.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-16 text-center">
                  <Icon name="FileX" size={40} className="text-muted-foreground mb-3" />
                  <p className="text-muted-foreground">Kayıt bulunamadı</p>
                  {(statusFilter || search || dateFrom || dateTo) && (
                    <button onClick={handleClearFilters} className="mt-2 text-sm text-primary hover:underline">
                      Filtreleri temizle
                    </button>
                  )}
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="bg-muted/50 border-b border-border">
                        <th className="w-8 px-3 py-3"></th>
                        <th className="text-left px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wide">Durum</th>
                        <th className="text-left px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wide">Sipariş No</th>
                        <th className="text-left px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wide">Müşteri</th>
                        <th className="text-left px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wide">Belge No</th>
                        <th className="text-left px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wide">Tutar</th>
                        <th className="text-left px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wide">Son Hata Aşaması</th>
                        <th className="text-left px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wide">Son Deneme</th>
                        <th className="text-left px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wide"></th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-border">
                      {orderGroups?.map((group) => (
                        <OrderAccordionRow
                          key={group?.key}
                          group={group}
                          onViewDetail={setSelectedLog}
                          onRetry={handleRetry}
                          onSendEmail={handleSendEmail}
                          retryingId={retryingId}
                          sendingEmailId={sendingEmailId}
                          setEmailModalLog={setEmailModalLog}
                          setEmailInput={setEmailInput}
                        />
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="flex items-center justify-between px-5 py-4 border-t border-border">
                  <p className="text-sm text-muted-foreground">
                    Sayfa {page} / {totalPages} ({totalCount?.toLocaleString('tr-TR')} kayıt)
                  </p>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => setPage((p) => Math.max(1, p - 1))}
                      disabled={page === 1}
                      className="p-2 rounded-lg border border-border hover:bg-muted disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                    >
                      <Icon name="ChevronLeft" size={16} />
                    </button>
                    <button
                      onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                      disabled={page === totalPages}
                      className="p-2 rounded-lg border border-border hover:bg-muted disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                    >
                      <Icon name="ChevronRight" size={16} />
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* ── Tab: System Errors ── */}
          {activeTab === 'system' && (
            <div className="bg-card rounded-xl border border-border overflow-hidden">
              <div className="flex items-center gap-3 px-5 py-4 border-b border-border">
                <Icon name="AlertOctagon" size={16} className="text-orange-500" />
                <h2 className="text-base font-semibold text-foreground">
                  Sistem Hataları
                  <span className="ml-2 text-sm font-normal text-muted-foreground">
                    (sipariş detayı olmayan loglar)
                  </span>
                </h2>
              </div>

              {loading ? (
                <div className="flex items-center justify-center py-16">
                  <div className="w-8 h-8 border-4 border-primary/20 border-t-primary rounded-full animate-spin" />
                </div>
              ) : orphanLogs?.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-16 text-center">
                  <Icon name="CheckCircle" size={40} className="text-green-400 mb-3" />
                  <p className="text-muted-foreground font-medium">Sistem hatası bulunamadı</p>
                  <p className="text-sm text-muted-foreground mt-1">Tüm loglar bir siparişe bağlı.</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="bg-muted/50 border-b border-border">
                        <th className="text-left px-5 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wide">Durum</th>
                        <th className="text-left px-5 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wide">Log ID</th>
                        <th className="text-left px-5 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wide">Hata Aşaması</th>
                        <th className="text-left px-5 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wide">Hata Mesajı</th>
                        <th className="text-left px-5 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wide">Tarih</th>
                        <th className="text-left px-5 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wide"></th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-border">
                      {orphanLogs?.map((log) => (
                        <tr key={log?.id} className="hover:bg-muted/30 transition-colors">
                          <td className="px-5 py-3">
                            <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold border w-fit ${STATUS_LABELS?.[log?.status]?.color}`}>
                              <Icon
                                name={log?.status === 'success' ? 'CheckCircle' : log?.status === 'error' ? 'XCircle' : 'MinusCircle'}
                                size={12}
                              />
                              {STATUS_LABELS?.[log?.status]?.label || log?.status}
                            </span>
                          </td>
                          <td className="px-5 py-3 font-mono text-xs text-muted-foreground">{log?.id?.slice(0, 12)}…</td>
                          <td className="px-5 py-3">
                            {log?.error_stage ? (
                              <span className="text-xs text-orange-600 bg-orange-50 border border-orange-200 px-2 py-0.5 rounded">
                                {ERROR_STAGE_LABELS?.[log?.error_stage] || log?.error_stage}
                              </span>
                            ) : '-'}
                          </td>
                          <td className="px-5 py-3 text-muted-foreground text-xs max-w-xs truncate" title={log?.error_message}>
                            {log?.error_message || '-'}
                          </td>
                          <td className="px-5 py-3 text-muted-foreground text-xs whitespace-nowrap">
                            {log?.created_at ? new Date(log.created_at)?.toLocaleString('tr-TR') : '-'}
                          </td>
                          <td className="px-5 py-3">
                            <button
                              onClick={() => setSelectedLog(log)}
                              className="p-1.5 rounded-lg hover:bg-muted transition-colors text-muted-foreground hover:text-foreground"
                              title="Detay"
                            >
                              <Icon name="Eye" size={15} />
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

        </div>
      </div>

      {/* Modals */}
      {selectedLog && (
        <InvoiceDetailModal
          log={selectedLog}
          onClose={() => setSelectedLog(null)}
          onRetry={handleRetry}
          retryingId={retryingId}
        />
      )}
      {autoRetryResult && (
        <AutoRetryResultModal
          result={autoRetryResult}
          onClose={() => setAutoRetryResult(null)}
        />
      )}
      {emailModalLog && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-card rounded-2xl border border-border w-full max-w-md shadow-xl">
            <div className="flex items-center justify-between p-6 border-b border-border">
              <div>
                <h2 className="text-base font-semibold text-foreground">Müşteriye Gönder</h2>
                <p className="text-xs text-muted-foreground mt-0.5">{emailModalLog?.customer_name} — {emailModalLog?.doc_no || emailModalLog?.ettn?.slice(0, 12)}</p>
              </div>
              <button onClick={() => { setEmailModalLog(null); setEmailInput(''); }} className="p-2 rounded-lg hover:bg-muted transition-colors">
                <Icon name="X" size={16} />
              </button>
            </div>
            <div className="p-6 space-y-4">
              {emailModalLog?.email_sent && (
                <div className="flex items-center gap-2 bg-teal-50 border border-teal-200 rounded-lg px-3 py-2 text-sm text-teal-700">
                  <Icon name="CheckCircle" size={14} />
                  Bu fatura daha önce gönderildi. Tekrar gönderebilirsiniz.
                </div>
              )}
              <div>
                <label className="block text-sm font-medium text-foreground mb-1.5">Müşteri E-posta Adresi</label>
                <input
                  type="email"
                  value={emailInput}
                  onChange={(e) => setEmailInput(e?.target?.value)}
                  placeholder="ornek@email.com"
                  className="w-full px-3 py-2.5 text-sm bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20 text-foreground"
                />
              </div>
              <p className="text-xs text-muted-foreground">Mysoft'tan imzalı PDF çekilerek bu adrese gönderilecektir.</p>
            </div>
            <div className="px-6 pb-6 flex gap-3">
              <Button variant="outline" onClick={() => { setEmailModalLog(null); setEmailInput(''); }} className="flex-1">İptal</Button>
              <Button
                variant="default"
                iconName={sendingEmailId === emailModalLog?.id ? 'Loader' : 'Send'}
                iconPosition="left"
                disabled={!emailInput?.trim() || sendingEmailId === emailModalLog?.id}
                onClick={() => handleSendEmail(emailModalLog)}
                className="flex-1"
              >
                {sendingEmailId === emailModalLog?.id ? 'Gönderiliyor...' : 'Gönder'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default InvoiceAuditDashboard;
